import React, { useState, useEffect, useRef } from 'react';
import { Row, Col, Form, Button, Nav, Alert, Card, ProgressBar } from 'react-bootstrap';

import convertFileToWebP from "../../../utilities/convertFileToWebP";
import { countries } from "../../../utilities/countries";

// Import your API services
import api from "../../../../src/seller-apis/api";
import servicesApi from "../../../../src/seller-apis/servicesApi";

export const ProductImages = ({ formData, updateFormData, validationErrors, onNext, onPrev }) => {
  const [activeSubTab, setActiveSubTab] = useState('images');
  const [uploadProgress, setUploadProgress] = useState({});
  const [uploading, setUploading] = useState(false);
  const [videoShow, setVideoShow] = useState(false);
  const [currentVideoSrc, setCurrentVideoSrc] = useState('');
  
  // Individual upload states for each image slot
  const [uploadingImages, setUploadingImages] = useState({
    cover: false,
    image1: false,
    image2: false,
    image3: false,
    video: false
  });
  
  // Individual image states - store S3 paths
  const [coverImage, setCoverImage] = useState(formData.coverImage || '');
  const [image1, setImage1] = useState(formData.image1 || '');
  const [image2, setImage2] = useState(formData.image2 || '');
  const [image3, setImage3] = useState(formData.image3 || '');
  const [videoFile, setVideoFile] = useState(formData.videoFile || '');
  const [videoUrl, setVideoUrl] = useState(formData.videoUrl || '');
  
  // Store base64 previews that persist across tab navigation
  const [previewUrls, setPreviewUrls] = useState({
    cover: formData.coverImagePreview || '',
    image1: formData.image1Preview || '',
    image2: formData.image2Preview || '',
    image3: formData.image3Preview || '',
  });

  const vendorId = localStorage.getItem("vendorId") || "demo";
  
  // Ref for cover image to enable auto-scrolling
  const coverImageRef = useRef(null);

  // Auto-scroll to cover image if there's a validation error
  useEffect(() => {
    if (validationErrors?.coverImage && coverImageRef.current) {
      coverImageRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      });
      
      // Switch to images tab if not already there
      if (activeSubTab !== 'images') {
        setActiveSubTab('images');
      }
    }
  }, [validationErrors]);

  useEffect(() => {
    // Update formData when images change
    updateFormData('coverImage', coverImage);
    updateFormData('image1', image1);
    updateFormData('image2', image2);
    updateFormData('image3', image3);
    updateFormData('videoFile', videoFile);
    updateFormData('videoUrl', videoUrl);
    
    // IMPORTANT: Store preview URLs in formData so they persist across tabs
    updateFormData('coverImagePreview', previewUrls.cover);
    updateFormData('image1Preview', previewUrls.image1);
    updateFormData('image2Preview', previewUrls.image2);
    updateFormData('image3Preview', previewUrls.image3);
    
    // Update images array in the required format
    const imagesList = [
      { name: coverImage, isCover: true },
      { name: image1, isCover: false },
      { name: image2, isCover: false },
      { name: image3, isCover: false }
    ].filter(img => img.name);
    
    const images = imagesList.map((img, index) => ({
      baseImage: img.isCover,
      smallImage: false,
      thumbnail: false,
      visible: false,
      image: `${img.name}`,
      label: img.name
    }));
    
    updateFormData('images', images);
  }, [coverImage, image1, image2, image3, videoFile, videoUrl, previewUrls]);
  
  // Convert file to base64 for persistent preview
  const fileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });
  };
  
  const handleImageUpload = async (event, imageType) => {
  const file = event.target.files[0];
  if (!file) return;

  // Validate file size (5MB limit)
  if (file.size > 5 * 1024 * 1024) {
    alert('Image size must be less than 5MB');
    return;
  }

  // Validate file type
  if (!file.type.startsWith('image/')) {
    alert('Please select a valid image file');
    return;
  }

  try {
    // Convert to base64 for INSTANT and PERSISTENT preview
    const base64Preview = await fileToBase64(file);
    setPreviewUrls(prev => ({ ...prev, [imageType]: base64Preview }));
    
    // Set uploading state
    setUploadingImages(prev => ({ ...prev, [imageType]: true }));
    
    // Convert to WebP
    const convertedImg = await convertFileToWebP(file);
    const str = convertedImg.name;
    const extension = convertedImg.name.slice(convertedImg.name.lastIndexOf("."));
    const comImg = str.replace(/[^\w]/g, "");
    const imageFinal = `img_${vendorId}_${Date.now()}_${comImg}${extension}`;

    const data = {
      imageName: imageFinal,
      type: "products",
    };

    // Get signed URL for upload
    const response = await api.product.saveProductPackageImage(data);
    
    // ✅ FIX: Handle both response formats safely
    let uploadUrl, finalUrl;
    
    if (typeof response.data === 'string') {
      // Old format: data is directly the URL string
      uploadUrl = response.data;
      finalUrl = `https://s3.toolsvilla.com/products/${imageFinal}`;
    } else if (response.data && typeof response.data === 'object') {
      // New format: data is an object with uploadUrl and finalUrl
      uploadUrl = response.data.uploadUrl;
      finalUrl = response.data.finalUrl || `https://s3.toolsvilla.com/products/${imageFinal}`;
    } else {
      throw new Error('Invalid response format from server');
    }

    if (!uploadUrl) {
      throw new Error('Upload URL not received from server');
    }
    
    // Upload to S3 using the uploadUrl
    const uploadResponse = await fetch(uploadUrl, {
      method: 'PUT',
      body: convertedImg,
      headers: {
        'Content-Type': convertedImg.type,
      },
    });

    if (uploadResponse.ok) {
      // Store just the path
      const imagePath = `products/${imageFinal}`;
      
      // Update the appropriate image state
      switch (imageType) {
        case 'cover':
          setCoverImage(imagePath);
          break;
        case 'image1':
          setImage1(imagePath);
          break;
        case 'image2':
          setImage2(imagePath);
          break;
        case 'image3':
          setImage3(imagePath);
          break;
      }
      
      console.log('Image uploaded successfully:', finalUrl);
      
    } else {
      const errorText = await uploadResponse.text();
      console.error('Upload failed:', errorText);
      
      // Clear preview on error
      setPreviewUrls(prev => ({ ...prev, [imageType]: '' }));
      
      throw new Error(`Upload failed with status ${uploadResponse.status}`);
    }
  } catch (error) {
    console.error('Error uploading image:', error);
    alert(`Failed to upload image: ${error.message}`);
    
    // Clear preview on error
    setPreviewUrls(prev => ({ ...prev, [imageType]: '' }));
  } finally {
    // Clear uploading state
    setUploadingImages(prev => ({ ...prev, [imageType]: false }));
  }
};



  const handleVideoUpload = async (event) => {
  const file = event.target.files[0];
  if (!file) return;

  // Validate file size (15MB limit)
  if (file.size > 15 * 1024 * 1024) {
    alert('Video size must be less than 15MB');
    return;
  }

  // Validate file type
  if (!file.type.startsWith('video/') || !file.type.includes('mp4')) {
    alert('Please select a valid MP4 video file');
    return;
  }

  try {
    setUploadingImages(prev => ({ ...prev, video: true }));
    
    const str = file.name;
    const extension = file.name.slice(file.name.lastIndexOf("."));
    const comImg = str.replace(/[^\w]/g, "");
    const videoFinal = `vid_${vendorId}_${Date.now()}_${comImg}${extension}`;

    const data = {
      imageName: videoFinal,
      type: "products",
      bucket: "m",
    };

    // Get signed URL for video upload
    const response = await api.product.saveProductPackageImage(data);
    
    // ✅ FIX: Handle both response formats safely
    let uploadUrl, finalUrl;
    
    if (typeof response.data === 'string') {
      // Old format: data is directly the URL string
      uploadUrl = response.data;
      finalUrl = `https://media.toolsvilla.com/products/${videoFinal}`;
    } else if (response.data && typeof response.data === 'object') {
      // New format: data is an object with uploadUrl and finalUrl
      uploadUrl = response.data.uploadUrl;
      finalUrl = response.data.finalUrl || `https://media.toolsvilla.com/products/${videoFinal}`;
    } else {
      throw new Error('Invalid response format from server');
    }

    if (!uploadUrl) {
      throw new Error('Upload URL not received from server');
    }
    
    // Upload to S3 using uploadUrl
    const uploadResponse = await fetch(uploadUrl, {
      method: 'PUT',
      body: file,
      headers: {
        'Content-Type': file.type,
      },
    });

    if (uploadResponse.ok) {
      setVideoFile(`products/${videoFinal}`);
      setVideoUrl(''); // Clear URL if file is uploaded
      console.log('Video uploaded successfully:', finalUrl);
    } else {
      const errorText = await uploadResponse.text();
      console.error('Upload failed:', errorText);
      throw new Error(`Upload failed with status ${uploadResponse.status}`);
    }
  } catch (error) {
    console.error('Error uploading video:', error);
    alert('Failed to upload video. Please try again.');
  } finally {
    setUploadingImages(prev => ({ ...prev, video: false }));
  }
};


  const deleteImage = (imageType) => {
    // Clear both preview and S3 path
    setPreviewUrls(prev => ({ ...prev, [imageType]: '' }));
    
    switch (imageType) {
      case 'cover':
        setCoverImage('');
        break;
      case 'image1':
        setImage1('');
        break;
      case 'image2':
        setImage2('');
        break;
      case 'image3':
        setImage3('');
        break;
    }
  };

  const deleteVideo = () => {
    setVideoFile('');
    setCurrentVideoSrc('');
    setVideoShow(false);
  };

  const playVideo = (videoSrc) => {
    setCurrentVideoSrc(videoSrc);
    setVideoShow(true);
  };

  const hidePlayer = () => {
    const video = document.getElementById('videoPlayer');
    if (video) {
      video.pause();
    }
    setVideoShow(false);
    setCurrentVideoSrc('');
  };

  const handleVideoUrlChange = (url) => {
    setVideoUrl(url);
    if (url) {
      setVideoFile(''); // Clear file if URL is provided
    }
  };

  const ImageUploadSlot = ({ label, imageState, imageType, required = false }) => {

    const getImageUrl = (imageName) => {
      // Priority 1: Use base64 preview if available (persists across tabs)
      if (previewUrls[imageType]) {
        return previewUrls[imageType];
      }
      
      // Priority 2: Use S3 URL if we have the path (for existing/saved images)
      if (!imageName) return null;
      imageName = imageName.trim();
      
      if (imageName.startsWith('http://') || imageName.startsWith('https://')) {
        return imageName;
      }
      
      if (imageName.startsWith('products/')) {
        return `https://s3.toolsvilla.com/${imageName}`;
      }
      
      return `https://s3.toolsvilla.com/products/${imageName}`;
    };

    const imageUrl = getImageUrl(imageState);
    const isUploading = uploadingImages[imageType];
    const hasImage = imageState || previewUrls[imageType];
    
    return (
      <div 
        className="image-upload-slot mb-4"
        ref={imageType === 'cover' ? coverImageRef : null}
      >
        <div className="d-flex justify-content-between align-items-center mb-0">
          <label className={`custom-form-input ${required && validationErrors?.coverImage && !imageState ? 'text-danger' : ''}`}>
            {label} {required && <span className="text-danger">*</span>}
          </label>
          {hasImage && !isUploading && (
            <Button
              variant="outline-danger"
              size="sm"
              onClick={() => deleteImage(imageType)}
              className='mb-2'
            >
              <i className="bi bi-trash"></i>
            </Button>
          )}
        </div>
        
        <Card className={`image-slot ${hasImage ? 'has-image' : 'empty-slot'} ${required && validationErrors?.coverImage && !imageState ? 'border-danger' : ''}`}>
          <Card.Body className="py-1 p-2 text-center position-relative">
            {isUploading ? (
              <div className="upload-loader">
                <div className="spinner-border text-success mb-3" role="status">
                  <span className="visually-hidden">Uploading...</span>
                </div>
                <p className="text-muted mb-0">Uploading image...</p>
                <small className="text-muted">Please wait</small>
              </div>
            ) : hasImage ? (
              <div className="uploaded-image">
                <img 
                  src={imageUrl}
                  alt={label}
                  className="img-fluid rounded"
                  style={{ maxHeight: '150px', objectFit: 'cover' }}
                  onError={(e) => {
                    console.error('Failed to load image:', imageUrl);
                  }}
                />
              </div>
            ) : (
              <div className="upload-placeholder">
                <i className="bi bi-image text-muted" style={{ fontSize: '2rem' }}></i>
                <p className="text-muted mb-2 mt-2">{label}</p>
                <Form.Control
                  type="file"
                  accept="image/*"
                  onChange={(e) => handleImageUpload(e, imageType)}
                  style={{ display: 'none' }}
                  id={`${imageType}-upload`}
                  disabled={isUploading}
                />
                <Button
                  as="label"
                  htmlFor={`${imageType}-upload`}
                  size="sm"
                  disabled={isUploading}
                  className="custom-upload-btn"
                >
                  <i className="bi bi-upload me-1"></i>
                  Choose Image
                </Button>
              </div>
            )}
          </Card.Body>
        </Card>
        
        {required && validationErrors?.coverImage && !imageState && (
          <div className="text-danger small mt-1">{validationErrors.coverImage}</div>
        )}
      </div>
    );
  }

  return (
    <div className="product-images-tab">
      {/* Video Player Modal */}
      {videoShow && (
        <div className="video-modal-overlay" onClick={hidePlayer}>
          <div className="video-modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="video-close-btn" onClick={hidePlayer}>
              <i className="bi bi-x-circle-fill"></i>
            </button>
            <video
              id="videoPlayer"
              src={currentVideoSrc}
              controls
              autoPlay
              className="modal-video"
            />
          </div>
        </div>
      )}

      {/* Sub Tabs */}
      <Row className="mb-4">
        <Col>
          <Nav variant="tabs" className="custom-nav-tabs border-0">
            <Nav.Item>
              <Nav.Link
                active={activeSubTab === 'images'}
                onClick={() => setActiveSubTab('images')}
                className={`custom-nav-link ${activeSubTab === 'images' ? 'active' : ''}`}
              >
                <i className="bi bi-image-fill me-2"></i>
                Product Images
              </Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link
                active={activeSubTab === 'video'}
                onClick={() => setActiveSubTab('video')}
                className={`custom-nav-link ${activeSubTab === 'video' ? 'active' : ''}`}
              >
                <i className="bi bi-camera-video me-2"></i>
                Product Video (Optional)
              </Nav.Link>
            </Nav.Item>
          </Nav>
        </Col>
      </Row>

      {/* Images Tab Content */}
      {activeSubTab === 'images' && (
        <Row className="g-4">
          <Col md={6}>
            <ImageUploadSlot
              label="Cover Photo"
              imageState={coverImage}
              imageType="cover"
              required={true}
            />
          </Col>
          <Col md={6}>
            <ImageUploadSlot
              label="Image 1"
              imageState={image1}
              imageType="image1"
            />
          </Col>
          <Col md={6}>
            <ImageUploadSlot
              label="Image 2"
              imageState={image2}
              imageType="image2"
            />
          </Col>
          <Col md={6}>
            <ImageUploadSlot
              label="Images 3"
              imageState={image3}
              imageType="image3"
            />
          </Col>
          <Col xs={12}>
            <Alert variant="info">
              <i className="bi bi-info-circle me-2"></i>
              <strong>Image Requirements:</strong> PNG, JPG, JPEG formats. Maximum 5MB per image. 
              High-resolution images (1200x1200px recommended) provide the best customer experience.
            </Alert>
          </Col>
        </Row>
      )}

      {/* Video Tab Content */}
      {activeSubTab === 'video' && (
        <Row className="g-4">
          {/* Upload Video File */}
          <Col md={6}>
            <Card className="h-100">
              <Card.Body className="py-1 px-2">
                <h5 className="form-control-sm custom-form-input mb-3">Upload Video File</h5>
                
                {uploadingImages.video ? (
                  <Card className="border-2 border-dashed">
                    <Card.Body className="text-center py-5">
                      <div className="spinner-border text-success mb-3" style={{ width: '3rem', height: '3rem' }} role="status">
                        <span className="visually-hidden">Uploading...</span>
                      </div>
                      <p className="text-muted mb-2 fw-semibold">Uploading video...</p>
                      <small className="text-muted">This may take a moment</small>
                    </Card.Body>
                  </Card>
                ) : videoFile ? (
                  <div className="uploaded-video-preview">
                    <Card className="border-success">
                      <Card.Body className="text-center">
                        <video
                          src={`https://media.toolsvilla.com/${videoFile}`}
                          className="preview-video mb-3"
                          style={{ maxWidth: '100%', maxHeight: '200px' }}
                          onClick={() => playVideo(`https://media.toolsvilla.com/${videoFile}`)}
                        />
                        <div>
                          <Button
                            size="sm"
                            className="me-2 custom-upload-btn"
                            onClick={() => playVideo(`https://media.toolsvilla.com/${videoFile}`)}
                          >
                            <i className="bi bi-play-fill me-1"></i>
                            Play Video
                          </Button>
                          <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={deleteVideo}
                          >
                            <i className="bi bi-trash me-1"></i>
                          </Button>
                        </div>
                      </Card.Body>
                    </Card>
                  </div>
                ) : (
                  <Card className="border-2 border-dashed">
                    <Card.Body className="px-2 text-center py-4">
                      <div className="mb-3">
                        <i className="bi bi-upload" style={{ fontSize: '2.5rem', color: '#155724' }}></i>
                      </div>
                      <p className="text-muted mb-3">Upload a video showcasing your product</p>
                      
                      <Form.Group>
                        <Form.Control
                          type="file"
                          accept="video/mp4"
                          onChange={handleVideoUpload}
                          style={{ display: 'none' }}
                          id="video-upload"
                          disabled={uploadingImages.video || videoUrl}
                        />
                        <Button
                          as="label"
                          htmlFor="video-upload"
                          className="px-4 py-2 custom-upload-btn"
                          disabled={uploadingImages.video || videoUrl}
                        >
                          <i className="bi bi-camera-video me-2"></i>
                          Choose Video
                        </Button>
                      </Form.Group>
                    </Card.Body>
                  </Card>
                )}
                
                <Alert variant="warning" className="mt-3 small">
                  <strong>Video Requirements:</strong><br />
                  • Size: Max 15 MB<br />
                  • Resolution: 1280x720p<br />
                  • Duration: 10-60 seconds<br />
                  • Format: MP4 only
                </Alert>
              </Card.Body>
            </Card>
          </Col>

          {/* Video URL */}
          <Col md={6}>
            <Card className="h-100">
              <Card.Body className="py-1 px-2">
                <h5 className="form-control-sm custom-form-input mb-1">Or Enter Video URL</h5>
                <Form.Group className="mb-3">
                  <Form.Control
                    type="url"
                    value={videoUrl}
                    onChange={(e) => handleVideoUrlChange(e.target.value)}
                    placeholder="Enter YouTube, Vimeo, or other video URL"
                    disabled={videoFile}
                  />
                  <Form.Text className="text-muted">
                    You can use YouTube, Vimeo, or direct video links
                  </Form.Text>
                </Form.Group>

                {videoUrl && (
                  <Alert variant="success" className="mt-3">
                    <i className="bi bi-check-circle me-2"></i>
                    Video URL added successfully!
                    <div className="mt-2">
                      <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={() => setVideoUrl('')}
                      >
                        <i className="bi bi-trash me-1"></i>                        
                      </Button>
                    </div>
                  </Alert>
                )}

                <Alert variant="info" className="mt-3 small">
                  <i className="bi bi-info-circle me-2"></i>
                  Video URL will override uploaded video file. Use either file upload or URL, not both.
                </Alert>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}

      <div className="d-flex justify-content-between mt-2">
        <Button 
          variant="secondary" 
          onClick={onPrev} 
          size="sm"
        >
          <i className="bi bi-arrow-left me-1"></i> Previous
        </Button>
        <Button  
          style={{ 
            backgroundColor: '#165b5b',
            borderColor: '#165b5b',
            color: 'white'
          }}
          onClick={onNext} 
          size="sm"
        >
          Save & Next <i className="bi bi-arrow-right ms-2"></i>
        </Button>
      </div>

      <style jsx>{`
        /* Custom Nav Tabs Styling */
        .custom-nav-tabs {
          border-bottom: 2px solid #dee2e6 !important;
          gap: 0.5rem;
        }
        
        .custom-nav-link {
          border: none !important;
          border-bottom: 3px solid transparent !important;
          background-color: transparent !important;
          color: #6c757d !important;
          font-weight: 500 !important;
          padding: 0.75rem 1.5rem !important;
          border-radius: 0 !important;
          transition: all 0.2s ease !important;
          cursor: pointer !important;
        }
        
        .custom-nav-link:hover {
          color: #165b5b !important;
          background-color: #f8f9fa !important;
        }
        
        .custom-nav-link.active {
          color: #165b5b !important;
          font-weight: 600 !important;
          border-bottom-color: #165b5b !important;
          background-color: transparent !important;
        }
        
        /* Upload Loader Styling */
        .upload-loader {
          padding: 40px 20px;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
        }
        
        .spinner-border-lg {
          width: 3rem;
          height: 3rem;
        }

        .custom-upload-btn {
          background-color: #d4edda !important;
          border-color: #c3e6cb !important;
          color: #155724 !important;
        }
        
        .custom-upload-btn:hover:not(:disabled) {
          background-color: #c3e6cb !important;
          border-color: #b1dfbb !important;
          color: #155724 !important;
        }
        
        .custom-upload-btn:focus,
        .custom-upload-btn:active,
        .custom-upload-btn:focus-visible {
          background-color: #c3e6cb !important;
          border-color: #c3e6cb !important;
          color: #155724 !important;
          outline: 2px solid #d4edda !important;
          outline-offset: 2px;
          box-shadow: 0 0 0 0.2rem rgba(212, 237, 218, 0.5) !important;
        }
        
        .image-slot {
          min-height: 180px;
          transition: all 0.3s ease;
          cursor: pointer;
        }
        
        .image-slot.empty-slot {
          border: 2px dashed #dee2e6;
          background-color: #f8f9fa;
        }
        
        .image-slot.empty-slot:hover {
          border-color: #c3e6cb;
          background-color: #f0f8f5;
        }
        
        .image-slot.has-image {
          border: 2px solid #28a745;
        }
        
        .image-slot.border-danger {
          border-color: #dc3545 !important;
        }
        
        .upload-placeholder {
          padding: 20px;
        }
        
        .uploaded-image {
          position: relative;
        }
        
        .video-modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background-color: rgba(0, 0, 0, 0.8);
          display: flex;
          justify-content: center;
          align-items: center;
          z-index: 1050;
        }
        
        .video-modal-content {
          position: relative;
          max-width: 90%;
          max-height: 90%;
        }
        
        .video-close-btn {
          position: absolute;
          top: -40px;
          right: -10px;
          background: none;
          border: none;
          color: white;
          font-size: 2rem;
          cursor: pointer;
          z-index: 1051;
        }
        
        .modal-video {
          max-width: 800px;
          max-height: 600px;
          width: 100%;
          height: auto;
        }
        
        .preview-video {
          cursor: pointer;
          border-radius: 8px;
        }
        
        .preview-video:hover {
          opacity: 0.8;
        }
      `}</style>
    </div>
  );
};